import { useQuery } from "@tanstack/react-query";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useCaseMapStatusChange } from "hooks/useCaseMapStatusChange";
import MachineApprovalStatusesService from "services/MachineApprovalStatus";
import { machineApprovalStatusesKey } from "./queryKeys";

/**
 * Applies an approval status to the machines it is given. Owns the mutation
 * rather than taking a handler, so a pin summary and the bulk bar differ only
 * in which ids they hand it.
 */
export function MachineApprovalSelect({
  value,
  machineIds,
  onDone,
  size = "sm",
}) {
  const { data: statuses = [] } = useQuery({
    queryKey: machineApprovalStatusesKey,
    queryFn: () => MachineApprovalStatusesService.getForFilters({}),
  });

  const statusMutation = useCaseMapStatusChange();

  const handleChange = statusId => {
    const status = statuses.find(option => option.id === statusId);
    // onDone clears the caller's selection, so it waits for the save: firing it
    // beside mutate() unmounts this control mid-flight, losing both the pending
    // state and the selection the analyst would need to retry a failure.
    statusMutation.mutate(
      { machineIds, statusId, statusName: status?.name },
      { onSuccess: () => onDone?.() }
    );
  };

  return (
    <Select
      value={value ?? null}
      onValueChange={handleChange}
      disabled={statusMutation.isPending}
    >
      <SelectTrigger
        size={size}
        aria-label="Approval status"
        className="tw:min-w-24"
      >
        <SelectValue>
          {id =>
            statuses.find(option => option.id === id)?.name || "Set status"
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false}>
        <SelectGroup>
          {statuses.map(status => (
            <SelectItem key={status.id} value={status.id}>
              {status.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
